import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { MINT_MIST } from '../../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const danger = '#c62828';

/* — Shell ——————————————————————————— */
export const AuthPage = styled('section')({
  display: 'flex',
  justifyContent: 'center',
  margin: '12px 0 48px',
  padding: '48px 24px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  '@media (max-width: 600px)': {
    margin: '8px 0 32px',
    padding: '20px 12px',
    borderRadius: 18,
  },
});

export const Card = styled('div')({
  width: '100%',
  maxWidth: 440,
  padding: '32px 32px 28px',
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.08)',
  textAlign: 'left',
  '@media (max-width: 600px)': {
    padding: '24px 18px 22px',
    borderRadius: 16,
  },
});

export const LogoMark = styled('img')({
  display: 'block',
  width: 44,
  height: 44,
  objectFit: 'contain',
  marginBottom: 16,
});

export const Title = styled('h1')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 6px',
  '@media (max-width: 600px)': {
    fontSize: '1.5rem',
  },
});

export const Lead = styled('p')({
  fontSize: '0.98rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 0 22px',
});

export const CardFooter = styled('div')({
  marginTop: 20,
  paddingTop: 16,
  borderTop: '1px solid rgba(26, 26, 46, 0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: '0.92rem',
  color: muted,
  textAlign: 'center',
  '& p': { margin: 0 },
});

/* — Form primitives ——————————————————— */
export const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const Field = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

export const FieldLabel = styled('label')({
  fontSize: '0.92rem',
  fontWeight: 700,
  color: ink,
});

export const TextInput = styled('input')({
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1.5px solid rgba(26, 26, 46, 0.12)',
  background: '#f7fafc',
  fontFamily: 'inherit',
  fontSize: '1rem',
  color: ink,
  transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: '#8a93a6' },
  '&:focus': {
    outline: 'none',
    background: '#ffffff',
    borderColor: blue,
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.12)',
  },
  '&[aria-invalid="true"]': { borderColor: danger },
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
});

/** Wraps a password input so the show/hide toggle sits inside it. */
export const PasswordWrap = styled('div')({
  position: 'relative',
  '& input': { paddingRight: 48 },
  '& .MuiIconButton-root': {
    position: 'absolute',
    top: '50%',
    right: 6,
    transform: 'translateY(-50%)',
    color: muted,
  },
});

export const FieldError = styled('p')({
  margin: 0,
  fontSize: '0.85rem',
  color: danger,
});

export const SubmitButton = styled('button')({
  width: '100%',
  marginTop: 4,
  padding: '12px 24px',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  transition: 'filter 0.15s ease, box-shadow 0.15s ease',
  '&:hover:not(:disabled)': {
    filter: 'brightness(1.05)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
  '&:focus-visible': {
    outline: `3px solid rgba(13, 78, 254, 0.35)`,
    outlineOffset: 2,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

/** Inline status message; `$variant` is `error` (default) or `success`. */
export const StatusMessage = styled('div', {
  shouldForwardProp: (prop) => prop !== '$variant',
})(({ $variant }) => ({
  padding: '12px 14px',
  borderRadius: 10,
  fontSize: '0.92rem',
  lineHeight: 1.45,
  ...($variant === 'success'
    ? {
        color: '#065f46',
        background: 'rgba(1, 235, 178, 0.14)',
        border: '1px solid rgba(1, 235, 178, 0.4)',
      }
    : {
        color: '#b71c1c',
        background: 'rgba(211, 47, 47, 0.08)',
        border: '1px solid rgba(211, 47, 47, 0.3)',
      }),
}));

export const TextLink = styled(RouterLink)({
  fontWeight: 600,
  color: blue,
  textDecoration: 'none',
  '&:hover, &:focus-visible': {
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
});

/** Live password checklist under a new-password field; `$met` turns an item teal. */
export const RuleList = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: '12px 14px',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '6px 12px',
  borderRadius: 10,
  background: '#f7fafc',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  '@media (max-width: 420px)': {
    gridTemplateColumns: '1fr',
  },
});

export const RuleItem = styled('li', {
  shouldForwardProp: (prop) => prop !== '$met',
})(({ $met }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.86rem',
  lineHeight: 1.35,
  color: $met ? ink : muted,
  '& svg': {
    flex: '0 0 auto',
    fontSize: 17,
    color: $met ? '#00a77e' : 'rgba(26, 26, 46, 0.25)',
  },
}));

/** Round status badge above a message (verify email, reset done); `$tone` is success, error or neutral. */
export const StatusBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})(({ $tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  marginBottom: 14,
  '& svg': { fontSize: 30 },
  ...($tone === 'success'
    ? { color: '#00a77e', background: 'rgba(1, 235, 178, 0.18)' }
    : $tone === 'error'
      ? { color: danger, background: 'rgba(211, 47, 47, 0.1)' }
      : { color: blue, background: 'rgba(13, 78, 254, 0.1)' }),
}));

export const BodyText = styled('p')({
  margin: '0 0 20px',
  fontSize: '1rem',
  lineHeight: 1.55,
  color: ink,
});

export const LoadingRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 0 4px',
  fontSize: '0.98rem',
  color: muted,
});
